import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] px-6">
      <div className="w-full max-w-sm fade-up">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#F0B90B]/30 bg-[#F0B90B]/10">
            <Coins className="h-4 w-4 text-[#F0B90B]" />
          </div>
          <div className="font-heading text-sm font-bold tracking-widest text-[#EAECEF]">
            AUREUS <span className="text-[#F0B90B]">GOLD FUND</span>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-[#EAECEF]">Choose a new password</h1>
        {!token ? (
          <div data-testid="reset-invalid-token" className="mt-6 rounded-md border border-[#F6465D]/40 bg-[#F6465D]/10 px-4 py-3 text-sm text-[#F6465D]">
            This reset link is invalid. Please request a new one.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && (
              <div data-testid="reset-error-message" className="rounded-md border border-[#F6465D]/40 bg-[#F6465D]/10 px-3 py-2 text-sm text-[#F6465D]">
                {error}
              </div>
            )}
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min. 6 characters)"
              className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2.5 text-sm text-[#EAECEF] placeholder:text-[#5E6673] focus:border-[#F0B90B]/50 focus:outline-none"
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2.5 text-sm text-[#EAECEF] placeholder:text-[#5E6673] focus:border-[#F0B90B]/50 focus:outline-none"
            />
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F] disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
        <div className="mt-6 text-center">
          <Link to="/login" data-testid="reset-back-to-login-link" className="text-xs text-[#848E9C] transition-colors hover:text-[#F0B90B]">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
